import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeModal, setModalType } from "../../redux/slices/uiSlice";
import { clearOtpVerificationData, setPhoneUpdateVerificationData } from "../../redux/slices/authSlice";
import { setUser } from "../../redux/slices/userSlice";
import { phoneLoginAPI, phoneRegisterAPI, setAuthToken, updateUserMobileNumberAPI } from "../../api/auth";
import { updateUserProfileAPI } from "../../api/user";
import { verifyPhoneOtp, sendPhoneOtp, getPhoneAuthConfirmationResult, requestNotificationPermissionOnGesture, isFirebaseInvalidOtpError } from "../../utils/firebaseConfig";
import { updateDeviceTokenAPI } from "../../api/notification";
import { getSendPhoneOtpToastMessage } from "../../utils/phoneValidation";
import type { RootState } from "../../redux/store";
import ModalWrapper from "./ModalWrapper";
import showToast from "../../hooks/useToast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Country code to flag emoji mapping
const COUNTRY_FLAGS: Record<string, string> = {
  "973": "🇧🇭", // Bahrain
  "965": "🇰🇼", // Kuwait
  "968": "🇴🇲", // Oman
  "974": "🇶🇦", // Qatar
  "966": "🇸🇦", // Saudi Arabia
  "971": "🇦🇪", // UAE
};

const OtpVerifyModal: React.FC = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(60);
  const [isVerifyingFirebase, setIsVerifyingFirebase] = useState(false);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];
  const navigate = useNavigate();
  // Get OTP verification data from Redux
  const otpVerificationData = useSelector((state: RootState) => state.auth.otpVerification);

  // Autofocus first OTP input when modal opens
  useEffect(() => {
    inputRefs[0].current?.focus();
  }, []);

  // Countdown timer for resend OTP
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleClose = () => {
    dispatch(clearOtpVerificationData());
    dispatch(closeModal());
  };

  const handleBack = () => {
    // Go back to the appropriate modal based on flow type
    if (otpVerificationData?.flowType === "updateMobile") {
      handleClose();
      return;
    }
    dispatch(setModalType(otpVerificationData?.flowType === "register" ? "register" : "login"));
  };

  // Determine which API to use based on flowType
  const isRegisterFlow = otpVerificationData?.flowType === "register";
  const isLoginFlow = otpVerificationData?.flowType === "login";
  const isUpdateMobileFlow = otpVerificationData?.flowType === "updateMobile";

  const handleVerificationSuccess = (data: any) => {
    const token = data?.data?.token;
    const user = data?.data?.user;


    if (token) {
      const rememberMe = otpVerificationData?.rememberMe ?? true;
      setAuthToken(token, rememberMe);

      // For mobile login flow, persist or clear saved phone details for next time
      if (isLoginFlow) {
        if (rememberMe && otpVerificationData?.phoneNumber && otpVerificationData?.countryCode) {
          localStorage.setItem("mobileLoginCountryCode", otpVerificationData.countryCode);
          localStorage.setItem("mobileLoginPhoneNumber", otpVerificationData.phoneNumber);
        } else {
          localStorage.removeItem("mobileLoginCountryCode");
          localStorage.removeItem("mobileLoginPhoneNumber");
        }
      }
    }

    if (user) {
      dispatch(setUser(user));
    }

    // Try to register fcm token if available after token is saved
    const fcmToken = localStorage.getItem("fcmToken");
    if (fcmToken) {
      updateDeviceTokenAPI(fcmToken, "web").catch(err => console.log("FCM registration error after login:", err));
    }

    showToast(isRegisterFlow ? t("messages.registrationSuccess") : t("messages.loginSuccess"), "success");
    dispatch(clearOtpVerificationData());
    dispatch(closeModal());

    // Navigate after ensuring token is stored
    setTimeout(() => {
      if (isRegisterFlow) {
        navigate("/my-account");
      } else {
        navigate("/create-game");
      }
    }, 100);
  };

  const isVerifying = isVerifyingFirebase;

  const [isResendingFirebase, setIsResendingFirebase] = useState(false);
  const isResending = isResendingFirebase;

  const isOtpComplete = otp.every((digit) => digit !== "");

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move to next input (always LTR)
    if (value && index < inputRefs.length - 1) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Backspace navigation (always LTR)
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
    // Trigger verification on Enter key
    if (e.key === "Enter" && isOtpComplete) {
      e.preventDefault();
      handleVerify();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    const newOtp = [...otp];

    for (let i = 0; i < pastedData.length; i++) {
      if (i < 6 && /^\d$/.test(pastedData[i])) {
        newOtp[i] = pastedData[i];
      }
    }
    setOtp(newOtp);

    // Focus the last filled input or the next empty one
    const lastFilledIndex = Math.min(pastedData.length - 1, 5);
    inputRefs[lastFilledIndex].current?.focus();
  };

  const handleVerify = async () => {
    const otpCode = otp.join("");
    if (otpCode.length !== 6 || !otpVerificationData || isVerifyingFirebase) return;

    // Safely request notification permission in user gesture without blocking submit
    requestNotificationPermissionOnGesture().then(async (fcmToken) => {
      if (fcmToken) {
        const jwtToken = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (jwtToken && jwtToken !== "undefined") {
          try {
            await updateDeviceTokenAPI(fcmToken, "web");
          } catch (err) {
            console.warn("FCM registration warning inside gesture handler:", err);
          }
        }
      }
    }).catch(err => console.log("Gesture FCM permission ignored:", err));

    const showInvalidOtp = () => {
      showToast(t("messages.invalidOtp"), "error");
    };

    // Update mobile flow: verify with Firebase and then call backend (verify-otp-firebase)
    if (isUpdateMobileFlow) {
      setIsVerifyingFirebase(true);
      try {
        if (!getPhoneAuthConfirmationResult()) {
          showInvalidOtp();
          return;
        }

        const { idToken } = await verifyPhoneOtp(otpCode);

        await updateUserMobileNumberAPI({ idToken });

        try {
          const formData = new FormData();
          formData.append("phone_number", otpVerificationData.phoneNumber);
          if (otpVerificationData.countryCode) {
            formData.append("country_code", otpVerificationData.countryCode);
          }
          await updateUserProfileAPI(formData);
        } catch (profileError) {
          console.error("Error updating profile with new mobile number:", profileError);
        }

        dispatch(
          setPhoneUpdateVerificationData({
            phoneNumber: otpVerificationData.phoneNumber,
            countryCode: otpVerificationData.countryCode,
          })
        );

        showToast(t("messages.otpVerificationSuccess"), "success");
        dispatch(clearOtpVerificationData());
        dispatch(closeModal());
      } catch (error: any) {
        console.log("Firebase OTP Verification Error:", error);
        showInvalidOtp();
      } finally {
        setIsVerifyingFirebase(false);
      }
      return;
    }

    // Login/register flow: verify with Firebase and then call backend (phone-login/phone-register)
    if (isLoginFlow || isRegisterFlow) {
      setIsVerifyingFirebase(true);
      try {
        if (!getPhoneAuthConfirmationResult()) {
          showInvalidOtp();
          return;
        }

        const { idToken } = await verifyPhoneOtp(otpCode);

        const data = isLoginFlow
          ? await phoneLoginAPI({
            idToken,
            country_code: otpVerificationData.countryCode,
            phone_number: otpVerificationData.phoneNumber,
          })
          : await phoneRegisterAPI({
            idToken,
            country_code: otpVerificationData.countryCode,
            phone_number: otpVerificationData.phoneNumber,
            full_name: otpVerificationData.fullName,
            dob: otpVerificationData.dob,
            gender: otpVerificationData.gender,
            profile_image: otpVerificationData.profileImage,
          });

        console.log(isLoginFlow ? "Phone Login Successful:" : "Phone Register Successful:", data);
        handleVerificationSuccess(data);
      } catch (error: any) {
        // Invalid/expired OTP (or missing session) must always show Invalid OTP — never "Failed to send OTP"
        if (isFirebaseInvalidOtpError(error) || !error?.response) {
          showInvalidOtp();
        } else {
          showToast(error?.message || t("messages.otpVerificationFailed"), "error");
        }
      } finally {
        setIsVerifyingFirebase(false);
      }
      return;
    }

    // Unknown flow / no OTP session — still show Invalid OTP on verify (never "Failed to send OTP")
    showInvalidOtp();
  };

  const handleResendOtp = async () => {
    if (!otpVerificationData?.phoneNumber) return;

    // Login/register flow: resend OTP via Firebase
    if (!recaptchaContainerRef.current) return;
    const fullPhoneNumber = `+${otpVerificationData.countryCode || ""}${otpVerificationData.phoneNumber}`;
    setIsResendingFirebase(true);
    // Clear filled OTP and focus first input box
    setOtp(["", "", "", "", "", ""]);
    inputRefs[0].current?.focus();
    try {
      await sendPhoneOtp(fullPhoneNumber, recaptchaContainerRef.current);
      showToast(t("messages.otpSentSuccess"), "success");
      setResendTimer(60);
    } catch (error: unknown) {
      showToast(getSendPhoneOtpToastMessage(error, t, { isResend: true }), "error");
    } finally {
      setIsResendingFirebase(false);
    }
  };

  // Mask the phone number for display
  const getMaskedPhoneNumber = () => {
    if (!otpVerificationData?.phoneNumber) return "+*** ** *** ****";
    const phone = otpVerificationData.phoneNumber;
    const countryCode = otpVerificationData.countryCode || "***";
    const flag = countryCode !== "***" ? COUNTRY_FLAGS[countryCode] || "" : "";
    if (phone.length < 4) return phone;
    const lastFour = phone.slice(-4);
    return `+${countryCode} ** *** ${lastFour}`;
  };

  return (
    <ModalWrapper onClose={handleClose} showBackButton onBack={handleBack}>
      {/* Invisible reCAPTCHA container for Firebase Phone Auth resend (login flow) */}
      <div ref={recaptchaContainerRef} id="recaptcha-container-otp-verify" className="hidden" aria-hidden="true" />
      {/* Header */}
      <div className="text-center lg:mb-[1.2vw] mb-4">
        <h1 className="lg:text-[1.9vw]/[2.2vw] text-2xl/6 font-semibold text-primary-orange lg:mb-[0.3vw] mb-2">
          {t("otp.enterOtpCode")}
        </h1>
        <p className="lg:text-[1.1vw]/[1.4vw] text-base/tight text-black-800 font-bold">
          {t("otp.enterCodeSent")} <span className="text-primary-orange">{getMaskedPhoneNumber()}</span>{" "}
          {t("otp.toVerifyAccount")}
        </p>
      </div>

      <div className="flex lg:gap-[0.5vw] gap-4 justify-center lg:mb-[1.1vw] md:mb-5 mb-4" dir="ltr">
        {otp.map((digit, actualIndex) => {
          return (
            <div key={actualIndex} className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex
    2xl:rounded-[0.26vw] rounded-[5px]
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px]
    transition-all border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]
  `}>
              <input
                ref={inputRefs[actualIndex]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(actualIndex, e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => handleKeyDown(actualIndex, e)}
                onPaste={actualIndex === 0 ? handlePaste : undefined}
                disabled={isVerifying}
                className="2xl:mt-[0.208vw] mt-1 text-center w-full  lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent"
              />
            </div>
          );
        })}
      </div>

      {/* Verify Button */}
      <button
        onClick={handleVerify}
        disabled={!isOtpComplete || isVerifying}
        className="w-full 2xl:rounded-[0.26vw] rounded-[5px] h-full flex
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] md:text-lg/tight text-base/tight font-bold text-primary-yellow bg-primary-yellow border-black-300 transition-all duration-200 cursor-pointer text-center disabled:opacity-50 disabled:cursor-not-allowed 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
      >
        <span className="2xl:pt-[0.7vw] pt-2.5 2xl:m-[0.208vw] m-1 !mt-0 2xl:py-[0.5vw] py-2 lg:px-[5%] px-4 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[0deg] !border-t-0 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[linear-gradient(180deg,#D76A4D_0%,#B35840_101.89%)] w-full h-full"><span className="skew-x-[10deg] block">{isVerifying ? t("otp.verifying") : t("otp.verifyCode")}</span></span>
      </button>
      <div className="text-center lg:mt-[1.1vw] md:mt-5 mt-4">
        <span className="lg:text-[1.1vw]/[1.3vw] text-base/tight text-black-800 font-bold">{t("otp.dontReceiveOtp")}</span>
        {resendTimer == 0 && (
          <button
            onClick={handleResendOtp}
            disabled={isResending || resendTimer > 0}
            className="lg:text-[1.1vw]/[1.3vw] text-base/tight font-bold text-primary-orange hover:underline cursor-pointer ps-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isResending ? t("otp.sending") : resendTimer > 0 ? t("otp.resendOtp") + " " : t("otp.resendOtp")}
          </button>
        )}
        <span className="lg:text-[1.1vw]/[1.3vw] text-base/tight text-black-800 font-bold">
          {resendTimer > 0 && ` (${resendTimer}${t("otp.seconds")})`}
        </span>
      </div>
    </ModalWrapper>
  );
};

export default OtpVerifyModal;
